import type { StudioForm } from './studioData';

const fieldClass = 'w-full text-sm border border-background-300 rounded-md px-3.5 py-2.5 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block font-label uppercase tracking-widest text-[11px] text-foreground-600 mb-1.5';

type Props = {
  form: StudioForm;
  onChange: (key: keyof StudioForm, value: string) => void;
};

export default function EditorWorkspace({ form, onChange }: Props) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
      <div className="px-5 py-3.5 border-b border-background-200 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center text-lg"><i className="ri-quill-pen-line"></i></span>
          <h3 className="font-heading font-bold text-xl text-foreground-950">Editorial workspace</h3>
        </div>
        <span className="inline-flex items-center gap-1.5 font-label font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 rounded-sm bg-accent-100 text-accent-900 border border-accent-300 whitespace-nowrap">
          <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-edit-2-line"></i></span>
          Unsaved — demo only
        </span>
      </div>

      <div className="p-5 space-y-4">
        <div>
          <label htmlFor="ss-headline" className={labelClass}>Headline</label>
          <input id="ss-headline" value={form.headline} onChange={(e) => onChange('headline', e.target.value)} placeholder="Article headline" className={fieldClass} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="ss-author" className={labelClass}>Author</label>
            <input id="ss-author" value={form.author} onChange={(e) => onChange('author', e.target.value)} placeholder="Byline" className={fieldClass} />
          </div>
          <div>
            <label htmlFor="ss-category" className={labelClass}>Category</label>
            <input id="ss-category" value={form.category} onChange={(e) => onChange('category', e.target.value)} placeholder="e.g. Tournament Coverage" className={fieldClass} />
          </div>
        </div>

        <div>
          <label htmlFor="ss-summary" className={labelClass}>Summary</label>
          <textarea id="ss-summary" rows={3} value={form.summary} onChange={(e) => onChange('summary', e.target.value)} placeholder="Short summary shown above the article" className={`${fieldClass} resize-y`}></textarea>
        </div>

        <div>
          <label htmlFor="ss-body" className={labelClass}>Article body</label>
          <textarea id="ss-body" rows={10} value={form.body} onChange={(e) => onChange('body', e.target.value)} placeholder="Write or paste the article body. Separate paragraphs with a blank line." className={`${fieldClass} resize-y font-mono text-xs leading-relaxed`}></textarea>
        </div>

        <div>
          <label htmlFor="ss-image" className={labelClass}>Featured image URL</label>
          <input id="ss-image" value={form.image} onChange={(e) => onChange('image', e.target.value)} placeholder="https://..." className={fieldClass} />
          {form.image && (
            <div className="mt-2 w-full h-28 rounded-md overflow-hidden border border-background-200">
              <img src={form.image} alt="Featured image preview" className="w-full h-full object-cover object-top" />
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-background-200 space-y-4">
          <p className="font-label uppercase tracking-widest text-[11px] font-bold text-foreground-700">SEO</p>
          <div>
            <label htmlFor="ss-seo-title" className={labelClass}>SEO title</label>
            <input id="ss-seo-title" value={form.seoTitle} onChange={(e) => onChange('seoTitle', e.target.value)} placeholder="Search result title" className={fieldClass} />
          </div>
          <div>
            <label htmlFor="ss-seo-desc" className={labelClass}>Meta description</label>
            <textarea id="ss-seo-desc" rows={2} value={form.metaDescription} onChange={(e) => onChange('metaDescription', e.target.value)} placeholder="Search result description" className={`${fieldClass} resize-y`}></textarea>
          </div>
          <div>
            <label htmlFor="ss-slug" className={labelClass}>URL slug</label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500 whitespace-nowrap font-mono">/news/</span>
              <input id="ss-slug" value={form.slug} onChange={(e) => onChange('slug', e.target.value)} placeholder="url-slug" className={`${fieldClass} font-mono text-xs`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}